import { WarningIcon } from "@chakra-ui/icons";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
} from "@chakra-ui/react";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  transactionOverrideAllocationCreateSchema,
  transactionOverrideAllocationFormData,
} from "features/fund-family/setup/fund/allocations/TransactionOverrideAllocationCreateSchema";
import { useAllocationRuleListSuspense } from "gen/endpoints/allocation-rule/allocation-rule";
import { useCreateAllocationRuleLookup } from "gen/endpoints/allocation-rule-lookup/allocation-rule-lookup";
import { useFundListSuspense } from "gen/endpoints/fund-entities/fund-entities";
import { useFundFamilyConfigRetrieve } from "gen/endpoints/fund-family-config/fund-family-config";
import { useTransactionCodeConfigListSuspense } from "gen/endpoints/transactions/transactions";
import { AllocationRuleReadData } from "gen/model/allocationRuleReadData";
import { isEqual } from "lodash-es";
import React, { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ButtonOrder } from "shared/components/button/ButtonOrder";
import { FormCancelButton } from "shared/components/button/FormCancelButton";
import { FormSubmitButton } from "shared/components/button/FormSubmitButton";
import Form from "shared/components/form/Form";
import { FormFieldPrefix } from "shared/components/form/FormFieldPrefix";
import { FormSelect } from "shared/components/input/select/Select";
import { SelectOption } from "shared/components/input/select/SelectOption";
import { useBoolean } from "usehooks-ts";

interface AddTransactionAllocationOverrideProps {
  fundFamilyId: string;
  onSuccess: () => void;
  onCloseComplete: () => void;
}

const AddTransactionAllocationOverrideModal: React.FC<
  AddTransactionAllocationOverrideProps
> = ({ fundFamilyId, onSuccess, onCloseComplete }) => {
  const { t } = useTranslation(["common", "fundFamily", "allocation"]);
  const [noOverridesSet, setNoOverridesSet] = useState<boolean>(false);
  const { data: transactionCodeNameData } =
    useTransactionCodeConfigListSuspense(fundFamilyId, undefined);

  const { data: fundEntityData } = useFundListSuspense({
    fund_family_ids: [fundFamilyId],
  });

  const { data: allocationData } = useAllocationRuleListSuspense(
    fundFamilyId,
    undefined,
  );

  const { data: fundFamilyConfigData } =
    useFundFamilyConfigRetrieve(fundFamilyId);

  const { mutateAsync: createAllocationRuleLookup } =
    useCreateAllocationRuleLookup();

  const [
    entityIdToAllocationRules,
    allocationIdToEntityId,
    entityIdToDefaultRuleId,
  ] = useMemo(
    () => [
      allocationData.reduce<Record<string, AllocationRuleReadData[]>>(
        (acc, allocation) => {
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
          if (acc[allocation.entity_id]) {
            acc[allocation.entity_id].push(allocation);
          } else {
            acc[allocation.entity_id] = [allocation];
          }
          return acc;
        },
        {},
      ),
      allocationData.reduce<Record<string, string>>((acc, allocation) => {
        acc[allocation.id] = allocation.entity_id;
        return acc;
      }, {}),
      allocationData.reduce<Record<string, string>>((acc, allocation) => {
        if (
          allocation.transaction_calculation_id ===
          fundFamilyConfigData?.config.default_allocation_calculation_id
        ) {
          acc[allocation.entity_id] = allocation.id;
        }
        return acc;
      }, {}),
    ],
    [allocationData, fundFamilyConfigData?.config],
  );

  const form = useForm<transactionOverrideAllocationFormData>({
    // @ts-expect-error TODO: Convert to zod
    resolver: yupResolver(transactionOverrideAllocationCreateSchema),
    defaultValues: {
      allocation_rule_lookups: [{}],
    },
  });

  const { value: isOpen, setFalse: close } = useBoolean(true);

  const onSubmit = async (data: transactionOverrideAllocationFormData) => {
    const formEntityIdToSelectedId = data.allocation_rule_lookups.reduce<
      Record<string, string>
    >((acc, allocation) => {
      acc[allocationIdToEntityId[allocation["entity_id"]]] =
        allocation["entity_id"];
      return acc;
    }, {});
    if (isEqual(formEntityIdToSelectedId, entityIdToDefaultRuleId)) {
      setNoOverridesSet(true);
    } else {
      const submitData = data.allocation_rule_lookups.map(
        (lookup_override) => ({
          fund_family_id: fundFamilyId,
          entity_id: allocationIdToEntityId[lookup_override["entity_id"]],
          rule_id: lookup_override["entity_id"],
          investment_id: null,
          transaction_code_id: data.transaction_code_id,
        }),
      );
      await createAllocationRuleLookup({
        fundFamilyId,
        data: { allocation_rule_lookups: submitData },
      });
      onSuccess();
      close();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={close}
      onCloseComplete={onCloseComplete}
      scrollBehavior="inside"
    >
      <ModalOverlay />
      <ModalContent>
        <Form form={form} onSubmit={onSubmit}>
          <ModalHeader>
            {t(
              "allocation:transactionOverride.configureTransactionAllocations",
            )}
          </ModalHeader>
          <ModalBody>
            <FormSelect
              title={t("allocation:transactionOverride.selectTransactionCode")}
              name="transaction_code_id"
            >
              {transactionCodeNameData.map(
                ({ transaction_code_id, rich_name }) => (
                  <SelectOption
                    value={transaction_code_id}
                    key={transaction_code_id}
                  >
                    {rich_name!}
                  </SelectOption>
                ),
              )}
            </FormSelect>
            <Text size="14px" pt="8px" pb="8px">
              {t("allocation:transactionOverride.addTransactionOverride")}
            </Text>
            {fundEntityData.map((fundEntity, index) => (
              <FormFieldPrefix
                key={fundEntity.id}
                prefix={`allocation_rule_lookups.${index}`}
              >
                <FormSelect
                  name="entity_id"
                  title={fundEntity.legal_name}
                  defaultValue={entityIdToDefaultRuleId[fundEntity.id]}
                >
                  {entityIdToAllocationRules[fundEntity.id].map(
                    (allocation_rule) => (
                      <SelectOption
                        value={allocation_rule.id}
                        key={allocation_rule.id}
                      >
                        {allocation_rule.name}
                      </SelectOption>
                    ),
                  )}
                </FormSelect>
              </FormFieldPrefix>
            ))}
            {noOverridesSet && (
              <Alert
                status="warning"
                mb="24px"
                data-ls-enabled={true}
                data-public={true}
              >
                <AlertIcon>
                  <WarningIcon />
                </AlertIcon>
                <AlertDescription>
                  {t("allocation:transactionOverride.noOverridesSet")}
                </AlertDescription>
              </Alert>
            )}
          </ModalBody>
          <ModalFooter gap={2}>
            <ButtonOrder
              dismiss={<FormCancelButton onClick={close} />}
              confirm={<FormSubmitButton>{t("common:save")}</FormSubmitButton>}
            />
          </ModalFooter>
        </Form>
      </ModalContent>
    </Modal>
  );
};

export default AddTransactionAllocationOverrideModal;
